(() => {
  const storageKey = 'avo.selectedSchool';
  const fundedCounterStorageKey = 'avo.fundedCounter';
  let fundedCounterAnimationFrame;
  let fundedCounterDisplayedValue = 0;

  const parseFundedCounterValue = (value) => Number(String(value || '').replace(/[^\d.-]/g, '')) || 0;

  const updateFundedCounterTargets = (value) => {
    if (!value) return;
    fundedCounterDisplayedValue = parseFundedCounterValue(value);
    document.querySelectorAll('[data-avo-funded-counter]').forEach((target) => {
      target.textContent = value;
    });
  };

  const animateFundedCounterTargets = (value, startValue = fundedCounterDisplayedValue) => {
    const endValue = parseFundedCounterValue(value);
    const prefix = String(value).match(/^[^\d-]*/)?.[0] || '$';
    if (!endValue || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      updateFundedCounterTargets(value);
      return;
    }

    window.cancelAnimationFrame(fundedCounterAnimationFrame);
    const startedAt = performance.now();
    const duration = 2000;
    const renderFrame = (now) => {
      const progress = Math.min((now - startedAt) / duration, 1);
      const easedProgress = 1 - Math.pow(1 - progress, 3);
      const currentValue = Math.round(startValue + ((endValue - startValue) * easedProgress));
      updateFundedCounterTargets(`${prefix}${currentValue.toLocaleString('en-US')}`);
      if (progress < 1) fundedCounterAnimationFrame = window.requestAnimationFrame(renderFrame);
    };
    fundedCounterAnimationFrame = window.requestAnimationFrame(renderFrame);
  };

  const initialiseFundedCounter = () => {
    let cachedValue;
    try {
      cachedValue = localStorage.getItem(fundedCounterStorageKey);
    } catch (_) {}
    if (cachedValue) animateFundedCounterTargets(cachedValue, 0);

    const source = document.querySelector('[data-avo-funded-counter-source]');
    if (!source || source.dataset.avoFundedCounterReady) return;
    source.dataset.avoFundedCounterReady = 'true';

    let commitTimer;
    const readSourceValue = () => {
      const value = source.querySelector('.es-counter-value')?.textContent.trim();
      if (!value || !/\d/.test(value)) return null;

      const prefix = source.querySelector('.es-counter-prefix')?.textContent.trim() || '$';
      return `${prefix}${value}`;
    };
    const commitLiveValue = () => {
      const fundedValue = readSourceValue();
      if (!fundedValue) return;

      if (cachedValue && parseFundedCounterValue(fundedValue) !== parseFundedCounterValue(cachedValue)) {
        animateFundedCounterTargets(fundedValue);
      } else if (!cachedValue) {
        updateFundedCounterTargets(fundedValue);
      }
      try {
        localStorage.setItem(fundedCounterStorageKey, fundedValue);
      } catch (_) {}
      cachedValue = fundedValue;
    };
    const syncSourceValue = () => {
      const fundedValue = readSourceValue();
      if (!cachedValue && fundedValue) updateFundedCounterTargets(fundedValue);

      window.clearTimeout(commitTimer);
      commitTimer = window.setTimeout(commitLiveValue, 300);
    };

    new MutationObserver(syncSourceValue).observe(source, {
      childList: true,
      subtree: true,
      characterData: true,
    });
    syncSourceValue();
  };

  const normaliseSchool = (link) => {
    const url = new URL(link.href, window.location.origin);
    const parts = url.pathname.split('/').filter(Boolean);
    const collectionIndex = parts.indexOf('collections');
    if (collectionIndex === -1 || !parts[collectionIndex + 1]) return null;

    return {
      handle: parts[collectionIndex + 1],
      title: link.dataset.schoolTitle || link.textContent.trim(),
      url: url.pathname,
    };
  };

  const storeSchool = (school) => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(school));
    } catch (_) {}
    document.cookie = `avo_school=${encodeURIComponent(school.handle)}; path=/; max-age=31536000; SameSite=Lax`;
  };

  const readSchool = () => {
    try {
      const school = JSON.parse(localStorage.getItem(storageKey));
      if (school && school.handle) return school;
    } catch (_) {}

    const match = document.cookie.match(/(?:^|; )avo_school=([^;]*)/);
    return match ? { handle: decodeURIComponent(match[1]) } : null;
  };

  const applySchool = () => {
    const school = readSchool();
    const handle = school?.handle || '';
    const safeHandle = handle.replace(/[^a-z0-9_-]/gi, '');
    document.documentElement.dataset.avoSchool = handle;

    document.querySelectorAll('[data-avo-school-section]').forEach((section) => {
      if (section.classList.contains('avo-school-picker')) {
        section.hidden = Boolean(handle);
        section.setAttribute('aria-hidden', handle ? 'true' : 'false');
        if (handle) return;
      }

      const overrideMatch = section.querySelector(`[data-avo-school-variant="${safeHandle}"]`);
      const fallbackMatch = section.querySelector(`[data-avo-school-fallback-variant="${safeHandle}"]`);
      const activeMatch = overrideMatch || fallbackMatch;
      const hasMatch = Boolean(handle && activeMatch);

      section.querySelectorAll('[data-avo-school-default]').forEach((element) => {
        element.hidden = hasMatch;
        element.setAttribute('aria-hidden', hasMatch ? 'true' : 'false');
      });
      section.querySelectorAll('[data-avo-school-variant], [data-avo-school-fallback-variant]').forEach((element) => {
        const isActive = hasMatch && element === activeMatch;
        element.hidden = !isActive;
        element.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });
    });
  };

  const enableDrag = (track) => {
    if (track.dataset.avoDragReady) return;
    track.dataset.avoDragReady = 'true';

    let pointerId;
    let startX = 0;
    let startScroll = 0;
    let moved = false;

    track.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      pointerId = event.pointerId;
      startX = event.clientX;
      startScroll = track.scrollLeft;
      moved = false;
    });

    track.addEventListener('pointermove', (event) => {
      if (event.pointerId !== pointerId) return;
      const distance = event.clientX - startX;
      if (Math.abs(distance) > 5 && !moved) {
        moved = true;
        track.setPointerCapture(pointerId);
        track.classList.add('is-dragging');
      }
      if (!moved) return;
      track.scrollLeft = startScroll - distance;
    });

    const finishDrag = (event) => {
      if (event.pointerId !== pointerId) return;
      track.classList.remove('is-dragging');
      if (track.hasPointerCapture(pointerId)) track.releasePointerCapture(pointerId);
      pointerId = undefined;
    };

    track.addEventListener('pointerup', finishDrag);
    track.addEventListener('pointercancel', finishDrag);
    track.addEventListener('dragstart', (event) => event.preventDefault());
    track.addEventListener('click', (event) => {
      if (!moved) return;
      event.preventDefault();
      event.stopPropagation();
      moved = false;
    }, true);
  };

  const initialiseRails = (scope = document) => {
    if (scope.matches?.('.product-card-slider--avo-navigation [data-slider]')) enableDrag(scope);
    scope.querySelectorAll('.product-card-slider--avo-navigation [data-slider]').forEach(enableDrag);
  };

  const schoolPickerLimit = () => {
    if (window.matchMedia('(max-width: 719px)').matches) return 9;
    if (window.matchMedia('(max-width: 900px)').matches) return 12;
    return 21;
  };

  const updateSchoolPicker = (picker) => {
    const toggle = picker.querySelector('[data-avo-school-picker-toggle]');
    if (!toggle) return;

    const itemCount = picker.querySelectorAll('.avo-school-picker__item').length;
    toggle.hidden = picker.classList.contains('is-expanded') || itemCount <= schoolPickerLimit();
  };

  const schoolSortKey = (item) => {
    const title = item.dataset.schoolTitle || item.textContent.trim();
    return title.replace(/^university\s+of\s+/i, '').trim();
  };

  const sortSchoolPicker = (picker) => {
    const grid = picker.querySelector('.avo-school-picker__grid');
    if (!grid) return;

    const items = Array.from(grid.children).filter((item) => item.classList.contains('avo-school-picker__item'));
    items.sort((firstItem, secondItem) => {
      const keyComparison = schoolSortKey(firstItem).localeCompare(schoolSortKey(secondItem), undefined, {
        sensitivity: 'base',
      });
      if (keyComparison !== 0) return keyComparison;

      const firstTitle = firstItem.dataset.schoolTitle || firstItem.textContent.trim();
      const secondTitle = secondItem.dataset.schoolTitle || secondItem.textContent.trim();
      return firstTitle.localeCompare(secondTitle, undefined, { sensitivity: 'base' });
    });
    items.forEach((item) => grid.appendChild(item));
  };

  const initialiseSchoolPickers = (scope = document) => {
    if (scope.matches?.('.avo-school-picker')) {
      sortSchoolPicker(scope);
      updateSchoolPicker(scope);
    }
    scope.querySelectorAll('.avo-school-picker').forEach((picker) => {
      sortSchoolPicker(picker);
      updateSchoolPicker(picker);
    });
  };

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-avo-school-picker-toggle]');
    if (!toggle) return;

    const picker = toggle.closest('.avo-school-picker');
    if (!picker) return;

    picker.classList.add('is-expanded');
    toggle.setAttribute('aria-expanded', 'true');
    toggle.hidden = true;
  });

  document.addEventListener('click', (event) => {
    const link = event.target.closest('a.avo-school-link, a[data-avo-school-link]');
    if (!link) return;
    const school = normaliseSchool(link);
    if (school) storeSchool(school);
  });

  document.addEventListener('click', (event) => {
    const button = event.target.closest('.product-card-slider--avo-navigation [data-slider-button-previous], .product-card-slider--avo-navigation [data-slider-button-next]');
    if (!button) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    const slider = button.closest('.product-card-slider');
    const track = slider?.querySelector('[data-slider]');
    const card = track?.querySelector('[data-slider-item]');
    if (!track || !card) return;

    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    const direction = button.hasAttribute('data-slider-button-next') ? 1 : -1;
    track.scrollBy({ left: direction * (card.getBoundingClientRect().width + gap) * 3, behavior: 'smooth' });
  }, true);

  document.addEventListener('click', (event) => {
    const swatch = event.target.closest('.avo-product-card [data-avo-product-swatch]');
    if (!swatch) return;

    event.preventDefault();
    event.stopPropagation();

    const card = swatch.closest('.avo-product-card');
    const variantsJson = card?.querySelector('[data-avo-product-variants]');
    if (!card || !variantsJson) return;

    let variants;
    try {
      variants = JSON.parse(variantsJson.textContent);
    } catch (_) {
      return;
    }

    const color = swatch.dataset.avoColorValue;
    const colorIndex = Number(card.dataset.avoColorOptionIndex);
    const sizeIndexValue = card.dataset.avoSizeOptionIndex;
    const sizeIndex = sizeIndexValue === '' ? null : Number(sizeIndexValue);
    const productTitle = card.dataset.avoProductTitle || 'product';

    card.querySelectorAll('[data-avo-product-swatch]').forEach((option) => {
      const isSelected = option === swatch;
      option.classList.toggle('is-selected', isSelected);
      option.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
    });

    card.querySelectorAll('[data-avo-size-value]').forEach((sizeButton) => {
      const size = sizeButton.dataset.avoSizeValue;
      const variant = variants.find((item) => item.options[colorIndex] === color && item.options[sizeIndex] === size);
      const isAvailable = Boolean(variant?.available);

      sizeButton.disabled = !isAvailable;
      sizeButton.classList.toggle('is-unavailable', !isAvailable);
      if (isAvailable) {
        sizeButton.dataset.avoQuickAddVariant = variant.id;
        sizeButton.setAttribute('aria-label', `Add ${productTitle}, ${color}, size ${size}, to cart`);
      } else {
        delete sizeButton.dataset.avoQuickAddVariant;
        sizeButton.setAttribute('aria-label', `${productTitle}, ${color}, size ${size}, sold out`);
      }
    });

    if (sizeIndex === null) {
      const quickAddButton = card.querySelector('.avo-product-card__quick-add--single .avo-product-card__size');
      const variant = variants.find((item) => item.options[colorIndex] === color && item.available)
        || variants.find((item) => item.options[colorIndex] === color);
      if (quickAddButton && variant) {
        quickAddButton.disabled = !variant.available;
        quickAddButton.classList.toggle('is-unavailable', !variant.available);
        if (variant.available) quickAddButton.dataset.avoQuickAddVariant = variant.id;
        else delete quickAddButton.dataset.avoQuickAddVariant;
      }
    }

    const productImage = card.querySelector('.avo-product-card__media-link img');
    if (productImage && swatch.dataset.avoImageSrc) {
      productImage.removeAttribute('srcset');
      productImage.src = swatch.dataset.avoImageSrc;
    }
  });

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('.avo-product-card [data-avo-quick-add-variant]');
    if (!button || button.classList.contains('is-loading')) return;

    event.preventDefault();
    event.stopPropagation();
    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');

    try {
      const addResponse = await fetch(`${window.PXUTheme.routes.cart_add_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: Number(button.dataset.avoQuickAddVariant), quantity: 1 }),
      });
      const lineItem = await addResponse.json();

      if (!addResponse.ok || lineItem.status) {
        button.dispatchEvent(new CustomEvent('product:add-to-cart:error', {
          detail: { error: lineItem },
          bubbles: true,
        }));
        return;
      }

      const cartResponse = await fetch(`${window.PXUTheme.routes.cart_url}.js`);
      const cart = await cartResponse.json();

      if (window.location.pathname === '/cart') {
        window.location.reload();
        return;
      }

      button.dispatchEvent(new CustomEvent('product:add-to-cart:success', {
        detail: { cart, line_item: lineItem },
        bubbles: true,
      }));

      const cartDrawer = window.Core?.Components.getComponent('cart-drawer');
      if (cartDrawer?.data.enabled) {
        cartDrawer.open(button, true, [lineItem]);
      } else {
        window.location = window.PXUTheme.routes.cart_url;
      }
    } catch (error) {
      console.error('Quick add error:', error);
    } finally {
      button.classList.remove('is-loading');
      button.removeAttribute('aria-busy');
    }
  });

  document.addEventListener('DOMContentLoaded', () => {
    applySchool();
    initialiseFundedCounter();
    initialiseRails();
    initialiseSchoolPickers();

    window.addEventListener('resize', () => {
      document.querySelectorAll('.avo-school-picker').forEach(updateSchoolPicker);
    });

    new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) initialiseRails(node);
        });
      });
    }).observe(document.body, { childList: true, subtree: true });
  });
  document.addEventListener('shopify:section:load', (event) => {
    applySchool();
    initialiseFundedCounter();
    initialiseRails(event.target);
    initialiseSchoolPickers(event.target);
  });
})();
